// PATH: SaberpDeals/frontend/src/pages/DealsPage.tsx
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dealsApi, type DealListItem, type DealDetail, type DealType } from '@/lib/dealsApi';

const typeTag: Record<DealType, string> = {
    New: 'bg-forest-soft text-forest',
    Upsell: 'bg-plum-soft text-plum',
    Renewal: 'bg-blue2-soft text-blue2',
    Support: 'bg-amber2-soft text-amber2',
};
const roleTag: Record<string, string> = {
    Company: 'bg-paper-2 text-ink-2',
    Owner: 'bg-plum-soft text-plum',
    Sales: 'bg-forest-soft text-forest',
    Technical: 'bg-amber2-soft text-amber2',
};

function money(n: number, cur: string) {
    const sym = cur === 'USD' ? '$' : 'IQD ';
    const dp = cur === 'USD' ? 2 : 0;
    return sym + (n || 0).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

const TYPES: (DealType | 'All')[] = ['All', 'New', 'Upsell', 'Renewal', 'Support'];

export default function DealsPage() {
    const [params] = useSearchParams();
    const highlight = params.get('highlight');

    const [deals, setDeals] = useState<DealListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<DealType | 'All'>('All');

    const [openId, setOpenId] = useState<number | null>(null);
    const [detail, setDetail] = useState<DealDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    async function load() {
        setLoading(true);
        try { setDeals(await dealsApi.list()); setError(null); }
        catch { setError('Failed to load deals.'); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    // Auto-open the just-saved deal.
    useEffect(() => {
        if (highlight && deals.some((d) => d.id === Number(highlight))) toggle(Number(highlight));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlight, deals.length]);

    async function toggle(id: number) {
        if (openId === id) { setOpenId(null); setDetail(null); return; }
        setOpenId(id); setDetail(null); setDetailLoading(true);
        try { setDetail(await dealsApi.get(id)); } catch { /* ignore */ }
        finally { setDetailLoading(false); }
    }

    async function remove(id: number) {
        if (!confirm('Delete this deal? Its frozen payout is removed too. This cannot be undone.')) return;
        try { await dealsApi.remove(id); if (openId === id) { setOpenId(null); setDetail(null); } await load(); }
        catch { /* ignore */ }
    }

    const shown = filter === 'All' ? deals : deals.filter((d) => d.dealType === filter);

    return (
        <div className="mt-6">
            <div className="mb-4">
                <div className="font-disp text-[21px] font-semibold tracking-tight">Deals</div>
                <div className="text-[13px] text-ink-3 mt-0.5">Every recorded sale. Click one to see items, the split, and the frozen payout.</div>
            </div>

            <div className="flex gap-2 mb-4 font-mono text-[12.5px] flex-wrap">
                {TYPES.map((t) => (
                    <button key={t} onClick={() => setFilter(t)}
                        className={`px-3 py-1.5 rounded-full ${filter === t ? 'bg-ink text-paper font-semibold' : 'bg-paper-2 text-ink-2'}`}>
                        {t}
                    </button>
                ))}
            </div>

            {error && <div className="mb-4 rounded-lg bg-clay-soft border border-clay/30 px-4 py-3 text-sm text-clay">{error}</div>}

            {loading ? (
                <div className="text-ink-3 text-sm py-16 text-center">Loading…</div>
            ) : shown.length === 0 ? (
                <div className="text-center py-20 text-ink-3">
                    <div className="font-disp text-4xl mb-3 text-line-2">§</div>
                    <div className="font-disp text-lg font-semibold text-ink-2 mb-1.5">No deals yet</div>
                    <div className="text-sm">Record your first sale on the New deal tab.</div>
                </div>
            ) : (
                shown.map((d) => (
                    <div key={d.id} className={`bg-card border rounded-2xl overflow-hidden mb-3 ${String(d.id) === highlight ? 'border-forest-2' : 'border-line'}`}>
                        <div className="flex justify-between items-start px-5 py-4 cursor-pointer hover:bg-paper" onClick={() => toggle(d.id)}>
                            <div>
                                <div className="font-disp text-[17px] font-semibold flex items-center gap-2">
                                    {d.customer}
                                    <span className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded-xl uppercase tracking-wide ${typeTag[d.dealType]}`}>{d.dealType}</span>
                                </div>
                                <div className="text-[12px] text-ink-3 font-mono mt-1">{d.date} · {d.currency}</div>
                            </div>
                            <div className="text-right font-mono">
                                <div className="text-[16px] font-semibold">{money(d.total, d.currency)}</div>
                                <div className="text-[11px] text-ink-3 mt-0.5">{d.recurringTotal > 0 ? `${money(d.recurringTotal, d.currency)}/yr recurring` : 'one-time'}</div>
                            </div>
                        </div>

                        {openId === d.id && (
                            <div className="border-t border-line px-5 py-4 bg-paper">
                                {detailLoading || !detail ? (
                                    <div className="text-ink-3 text-sm py-4">Loading detail…</div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <h4 className="font-mono text-[10.5px] font-semibold uppercase tracking-wider text-forest mb-2">Items sold</h4>
                                            <table className="w-full border-collapse">
                                                <tbody>
                                                    {detail.lines.map((l, i) => (
                                                        <tr key={i} className="border-b border-line last:border-0">
                                                            <td className="py-1.5 text-[13px]">
                                                                {l.itemName}
                                                                <span className="text-ink-3 font-mono text-[11px] ml-1.5">
                                                                    ×{l.qty}{l.lineDiscount > 0 ? ` −${money(l.lineDiscount, detail.currency)}` : ''}
                                                                    {l.billingType === 'Recurring' ? ' /yr' : ''}
                                                                </span>
                                                            </td>
                                                            <td className="py-1.5 text-right font-mono text-[13px]">{money(l.lineTotal, detail.currency)}</td>
                                                        </tr>
                                                    ))}
                                                    {detail.dealDiscount > 0 && (
                                                        <tr className="border-b border-line"><td className="py-1.5 text-[13px]">Deal discount</td><td className="py-1.5 text-right font-mono text-[13px]">−{money(detail.dealDiscount, detail.currency)}</td></tr>
                                                    )}
                                                    <tr><td className="py-1.5 text-[13px] font-semibold">Total</td><td className="py-1.5 text-right font-mono text-[13px] font-semibold">{money(detail.total, detail.currency)}</td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div>
                                            <h4 className="font-mono text-[10.5px] font-semibold uppercase tracking-wider text-forest mb-2">
                                                Payout — split {detail.split.company}/{detail.split.owner}/{detail.split.sales}/{detail.split.technical}
                                            </h4>
                                            <table className="w-full border-collapse">
                                                <tbody>
                                                    {detail.payouts.map((p, i) => (
                                                        <tr key={i} className="border-b border-line last:border-0">
                                                            <td className="py-1.5 text-[13px]">
                                                                {p.recipientName}
                                                                <span className={`text-[9.5px] font-mono font-semibold px-1.5 py-0.5 rounded-xl ml-1.5 uppercase ${roleTag[p.role]}`}>{p.role}</span>
                                                            </td>
                                                            <td className="py-1.5 text-right font-mono text-[13px]">{money(p.amount, detail.currency)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                                <div className="flex justify-end mt-4">
                                    <button onClick={() => remove(d.id)} className="text-[12.5px] font-medium text-ink-3 border border-line-2 rounded-lg px-3 py-1.5 hover:text-clay hover:border-clay hover:bg-clay-soft">Delete deal</button>
                                </div>
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    );
}
